import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios, { API, authHeaders, errText } from '../api';
import Layout from './Layout';
import { ui, statusBadge } from '../ui';
import { colors } from '../theme';
import DataTable from '../components/DataTable';
import PhotoLightbox from '../components/PhotoLightbox';
import MatchesBlock from '../components/MatchesBlock';
import QuestionnaireBlock from '../components/QuestionnaireBlock';
import SparkTransactionsBlock from '../components/SparkTransactionsBlock';
import { STATUS_LABEL, STATUS_TOOLTIP } from '../statusLabels';

function fmt(str) {
  return new Date(str).toLocaleDateString('ru', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export default function Applications() {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [lightboxUrl, setLightboxUrl] = useState(null);
  const [mainPhotoId, setMainPhotoId] = useState(null);
  const [error, setError] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();

  const fetchItems = useCallback(() => {
    axios.get(`${API}/admin/users`, {
      headers: authHeaders(),
      params: { search: search || undefined, status: status || undefined },
    }).then(r => setItems(r.data));
  }, [search, status]);
  useEffect(() => { fetchItems(); }, [fetchItems]);

  const openUser = (id) => {
    setSelectedId(id);
    setMainPhotoId(null);
    setError('');
    axios.get(`${API}/admin/users/${id}`, { headers: authHeaders() }).then(r => setDetail(r.data));
  };

  // Переход по ссылке "Перейти в анкету" из поп-апа слота (InterviewCalendar.js).
  useEffect(() => {
    const openId = searchParams.get('open');
    if (!openId) return;
    openUser(Number(openId));
    setSearchParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);
  const closeDetail = () => { setSelectedId(null); setDetail(null); fetchItems(); };

  const toggleBlock = async () => {
    const action = detail.status === 'blocked' ? 'unblock' : 'block';
    if (action === 'block' && !window.confirm('Заблокировать эту анкету?')) return;
    const r = await axios.post(`${API}/admin/users/${selectedId}/${action}`, {}, { headers: authHeaders() });
    setDetail(r.data);
  };

  const activate = async () => {
    if (!mainPhotoId) { setError('Выберите главное фото'); return; }
    setError('');
    try {
      const r = await axios.post(`${API}/admin/users/${selectedId}/activate`, { main_photo_id: mainPhotoId }, { headers: authHeaders() });
      setDetail(r.data);
    } catch (e) {
      setError(errText(e, 'Не удалось активировать'));
    }
  };

  const addPhoto = async (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;
    setError('');
    const form = new FormData();
    form.append('file', file);
    try {
      const r = await axios.post(`${API}/admin/users/${selectedId}/photos`, form, { headers: authHeaders() });
      setDetail(r.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Не удалось загрузить фото');
    }
  };

  const deletePhoto = async (photoId) => {
    if (!window.confirm('Точно удалить это фото? Восстановить его будет нельзя.')) return;
    setError('');
    try {
      const r = await axios.delete(`${API}/admin/users/${selectedId}/photos/${photoId}`, { headers: authHeaders() });
      setDetail(r.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Не удалось удалить фото');
    }
  };

  const setMainPhoto = async (photoId) => {
    setError('');
    try {
      const r = await axios.post(`${API}/admin/users/${selectedId}/photos/${photoId}/set-main`, {}, { headers: authHeaders() });
      setDetail(r.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Не удалось сделать фото главным');
    }
  };

  if (selectedId) {
    return (
      <Layout>
        <button style={ui.btnSecondary} onClick={closeDetail}>← Все анкеты</button>
        {!detail ? <div style={ui.empty}>Загрузка…</div> : (
          <>
            <h1 style={{ ...ui.title, marginTop: 20 }}>{detail.first_name || 'Без имени'}, {detail.phone}</h1>

            <div style={ui.card}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, fontSize: 14 }}>
                <div><b>ID:</b> {detail.id}</div>
                <div><b>Статус:</b> {STATUS_LABEL[detail.status] || detail.status}</div>
                <div><b>Пол:</b> {detail.gender || '—'}</div>
                <div><b>Город:</b> {detail.city || '—'}</div>
                <div><b>Рост:</b> {detail.height || '—'}</div>
                <div><b>Семейное положение:</b> {detail.marital_status || '—'}</div>
                <div><b>Искры:</b> {detail.spark_balance}</div>
                <div><b>Куратор:</b> {detail.curator_name || '—'}</div>
                <div><b>Дата регистрации:</b> {fmt(detail.created_at)}</div>
                {detail.referred_by_phone && (
                  <div><b>Пригласил:</b> {detail.referred_by_name || 'Без имени'}, {detail.referred_by_phone}</div>
                )}
              </div>
              {detail.short_bio && <div style={{ marginTop: 12 }}><b>О себе:</b> {detail.short_bio}</div>}
            </div>

            {detail.status !== 'deleted' && (() => { const activating = detail.status === 'awaiting_interview' || detail.status === 'no_show'; return (
            <div style={ui.card}>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>
                Фото {activating ? '— нажмите на фото, чтобы выбрать главное для активации' : ''}
              </div>
              <div style={{ color: colors.textMuted, fontSize: 13, marginBottom: 12 }}>
                Главное фото отмечено золотой рамкой и подписью «Главное» — его нельзя удалить, пока оно главное.
                Чтобы заменить, сделайте главным другое фото или загрузите новое.
              </div>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                {detail.photos.length === 0 && <div style={ui.empty}>Фото не загружены</div>}
                {detail.photos.map(p => (
                  <div key={p.id} style={{ width: 120, flexShrink: 0 }}>
                    <div
                      onClick={() => activating && setMainPhotoId(p.id)}
                      style={{
                        width: 120, height: 120, borderRadius: 8, overflow: 'hidden', position: 'relative',
                        cursor: activating ? 'pointer' : 'default',
                        border: `3px solid ${mainPhotoId === p.id || p.is_main ? colors.accent : colors.border}`,
                      }}>
                      <img src={`${API}${p.url}`} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      {p.is_main && <div style={{ position: 'absolute', bottom: 4, left: 4, right: 4, background: colors.accent, color: colors.bg, fontSize: 10, fontWeight: 700, textAlign: 'center', borderRadius: 4, padding: '2px 0' }}>Главное</div>}
                      <button
                        onClick={(e) => { e.stopPropagation(); setLightboxUrl(`${API}${p.url}`); }}
                        title="Открыть на весь экран"
                        style={{
                          position: 'absolute', top: 4, right: 4, width: 24, height: 24, borderRadius: 6,
                          background: 'rgba(6,10,22,0.6)', border: 'none', color: colors.text, fontSize: 13, cursor: 'zoom-in',
                        }}
                      >⤢</button>
                    </div>
                    {!activating && (
                      <div style={{ display: 'flex', gap: 4, marginTop: 6, fontSize: 11 }}>
                        {!p.is_main && (
                          <button style={{ ...ui.btnSecondary, padding: '4px 6px', fontSize: 11 }} onClick={() => setMainPhoto(p.id)}>Сделать главным</button>
                        )}
                        {!p.is_locked && (
                          <button style={{ ...ui.btnDanger, padding: '4px 6px', fontSize: 11 }} onClick={() => deletePhoto(p.id)}>Удалить</button>
                        )}
                      </div>
                    )}
                  </div>
                ))}
                <label style={{
                  width: 120, height: 120, borderRadius: 8, border: `2px dashed ${colors.border}`, flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', textAlign: 'center',
                  fontSize: 12, color: colors.textMuted, padding: 8,
                }}>
                  + Добавить фото
                  <input type="file" accept="image/jpeg,image/png,image/webp" onChange={addPhoto} style={{ display: 'none' }} />
                </label>
              </div>

              {error && <div style={{ ...ui.error, marginTop: 16 }}>{error}</div>}
              {activating && (
                <div style={{ marginTop: 16 }}>
                  <button style={ui.btn} onClick={activate}>Активировать анкету</button>
                </div>
              )}
            </div>
            ); })()}
            <PhotoLightbox url={lightboxUrl} onClose={() => setLightboxUrl(null)} />

            <MatchesBlock userId={detail.id} matches={detail.matches} profilePath="/applications" />
            <SparkTransactionsBlock transactions={detail.spark_transactions} />
            {detail.status !== 'deleted' && <QuestionnaireBlock questionnaire={detail.questionnaire} />}

            <div style={ui.card}>
              {detail.status === 'blocked'
                ? <button style={ui.btnSecondary} onClick={toggleBlock}>Разблокировать</button>
                : <button style={ui.btnDanger} onClick={toggleBlock}>Заблокировать анкету</button>}
            </div>
          </>
        )}
      </Layout>
    );
  }

  return (
    <Layout>
      <h1 style={ui.title}>Анкеты</h1>

      <div style={ui.row}>
        <input style={ui.input} placeholder="Поиск по имени, телефону или ID" value={search} onChange={e => setSearch(e.target.value)} />
        <select style={ui.input} value={status} onChange={e => setStatus(e.target.value)}>
          <option value="">Все статусы</option>
          {Object.entries(STATUS_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>

      <div style={ui.card}>
        <DataTable
          rows={items}
          rowKey={u => u.id}
          onRowClick={u => openUser(u.id)}
          emptyText="Анкет не найдено"
          columns={[
            { key: 'id', label: 'ID' },
            { key: 'first_name', label: 'Имя', render: u => u.first_name || '—' },
            { key: 'phone', label: 'Телефон' },
            {
              key: 'status',
              label: <span title={STATUS_TOOLTIP}>Статус ⓘ</span>,
              sortValue: u => STATUS_LABEL[u.status] || u.status,
              render: u => {
                const [bg, fg] = statusBadge[u.status] || [colors.surface2, colors.textMuted];
                return <span style={ui.badge(bg, fg)}>{STATUS_LABEL[u.status] || u.status}</span>;
              },
            },
            { key: 'curator_name', label: 'Куратор', render: u => u.curator_name || '—' },
            { key: 'spark_balance', label: 'Искры' },
            { key: 'matches_count', label: 'Метчи' },
            { key: 'created_at', label: 'Регистрация', render: u => fmt(u.created_at) },
          ]}
        />
      </div>
    </Layout>
  );
}
